/**
 * 기간 프리셋 하이라이트 — 달력을 열었을 때 지금 선택된 기간과 같은 프리셋(예: "최근 6개월")을 강조한다.
 * (2026-10-12 사용자: "현재 일치하는 게 있으면 해당하는 부분에 하이라이팅")
 *
 * 프리셋 픽커가 20곳이라 픽커마다 고치지 않고 앱 부팅 때 한 번 설치한다(`createMesFeApp`).
 * 달력 팝퍼는 body 로 teleport 되고 픽커 값에 직접 닿을 수 없으므로, 마지막으로 연 범위 픽커의
 * 입력칸(시작·끝, 표시 형식 YYYY-MM-DD)을 읽어 `DATE_RANGE_SHORTCUTS` 의 값과 비교한다.
 * 같은 기간이 여러 프리셋과 맞으면(1일의 "오늘"="이번 달") 모두 강조한다.
 */
import dayjs from 'dayjs';

import {
  DATE_RANGE_SHORTCUTS,
  DATE_RANGE_SHORTCUTS_POPPER_CLASS,
  type DateRangeShortcut,
} from './date-range-shortcuts.js';

export const ACTIVE_SHORTCUT_CLASS = 'is-active-preset';

/** 선택 기간과 같은 프리셋의 순번들 — 순수 함수(테스트 대상) */
export function matchingShortcutIndexes(
  range: readonly [string, string] | null,
  shortcuts: readonly DateRangeShortcut[] = DATE_RANGE_SHORTCUTS,
): number[] {
  if (!range || !range[0] || !range[1]) return [];
  const out: number[] = [];
  shortcuts.forEach((s, i) => {
    const [a, b] = s.value();
    if (dayjs(a).format('YYYY-MM-DD') === range[0] && dayjs(b).format('YYYY-MM-DD') === range[1]) {
      out.push(i);
    }
  });
  return out;
}

function readEditorRange(editor: Element | null): [string, string] | null {
  if (!editor) return null;
  const inputs = editor.querySelectorAll<HTMLInputElement>('input.el-range-input');
  if (inputs.length < 2) return null;
  return [inputs[0]!.value.trim(), inputs[1]!.value.trim()];
}

function paint(editor: Element | null): void {
  const poppers = document.querySelectorAll<HTMLElement>(
    `.el-popper.${DATE_RANGE_SHORTCUTS_POPPER_CLASS}`,
  );
  const active = new Set(matchingShortcutIndexes(readEditorRange(editor)));
  poppers.forEach((popper) => {
    popper.querySelectorAll('.el-picker-panel__shortcut').forEach((btn, i) => {
      btn.classList.toggle(ACTIVE_SHORTCUT_CLASS, active.has(i));
    });
  });
}

let installed = false;

/** 앱 부팅 시 1회 — 범위 픽커를 열거나 달력 안을 누를 때마다 다시 칠한다 */
export function installDateRangeShortcutHighlight(): void {
  if (installed || typeof document === 'undefined') return;
  installed = true;
  let lastEditor: Element | null = null;
  const schedule = () => {
    // 팝퍼가 그려진 뒤·선택값이 입력칸에 반영된 뒤 칠한다
    requestAnimationFrame(() => paint(lastEditor));
    window.setTimeout(() => paint(lastEditor), 120);
  };
  const onPointer = (e: Event) => {
    const target = e.target as Element | null;
    if (!target) return;
    const editor = target.closest('.el-range-editor');
    if (editor) {
      lastEditor = editor;
      schedule();
      return;
    }
    if (target.closest(`.${DATE_RANGE_SHORTCUTS_POPPER_CLASS}`)) schedule();
  };
  document.addEventListener('mousedown', onPointer, true);
  document.addEventListener('focusin', onPointer, true);
  document.addEventListener('click', onPointer, true);
}
